import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import RenderAnimation from '@components/RenderAnimation';
import cx from 'classnames';
import { gsap } from 'gsap';
import { breakpoints } from '../../../../hooks/useBreakpoints';
import s from './QuizTimer.module.scss';
import animationData from '../../../../animations/liquid.json';
import { useWindowWidth } from '../../../../hooks/windowHooks';

const propTypes = {
  children: PropTypes.node,
  secondsToAnswer: PropTypes.number.isRequired,
  onNextQuestion: PropTypes.func,
  onUnanswered: PropTypes.func,
  isAnimationStopped: PropTypes.bool.isRequired,
  setIsAnimationStopped: PropTypes.func,
  timeIsUp: PropTypes.bool,
  setTimeIsUp: PropTypes.func,
  isModalActive: PropTypes.bool,
  setTimer: PropTypes.func,
  isReadyToShow: PropTypes.bool,
  quizStarted: PropTypes.bool,
};

/* istanbul ignore next */
const defaultFunc = () => {};

const QuizTimer = ({
  children = <></>,
  onNextQuestion = defaultFunc,
  onUnanswered = defaultFunc,
  isAnimationStopped,
  setIsAnimationStopped = defaultFunc,
  timeIsUp = false,
  setTimeIsUp = defaultFunc,
  secondsToAnswer,
  isModalActive = false,
  setTimer = defaultFunc,
  isReadyToShow = false,
  quizStarted = false,
}) => {
  const wrapper = useRef(null);
  const container = useRef(null);
  const timelineRef = useRef(null);
  const startTime = useRef(0);
  const [timerComplete, setTimerComplete] = useState(false);
  const secondCount = useRef(0);
  const timelineCount = useRef(0);
  const pausedCount = useRef(0);
  const width = useWindowWidth();
  const [heightOffset, setHeightOffset] = useState(0);
  const hasTimedOut = useRef(false);
  const [hideAnimation, setHideAnimation] = useState(secondsToAnswer === 0);
  const [hideTimeoutScreen, setHideTimeoutScreen] = useState(false);
  const [started, setStarted] = useState(false);
  const [isTimed] = useState(secondsToAnswer !== 0);

  useEffect(() => {
    timelineRef.current = gsap.timeline();
  }, []);

  if (timeIsUp) {
    hasTimedOut.current = true;
  }

  useEffect(() => {
    setHeightOffset(() => {
      if (width > breakpoints.lg) {
        return '-17%';
      }
      if (width > breakpoints.xm) {
        return '-11.5%';
      }
      if (width > breakpoints.md) {
        return '-11%';
      }
      if (width > breakpoints.sm) {
        return '-9.8%';
      }
      return '-9.5%';
    });
  }, [width]);

  // main timer
  useEffect(() => {
    let secondInterval;
    if (isReadyToShow) {
      if (isAnimationStopped) {
        clearInterval(secondInterval);
      }

      if (!timerComplete && !isAnimationStopped) {
        if (secondCount.current === 0) {
          startTime.current = new Date(Date.now() - pausedCount.current);
        }
        secondInterval = setInterval(() => {
          if (isModalActive) {
            clearInterval(secondInterval);
            pausedCount.current = secondCount.current * 1000;
            secondCount.current = 0;
          } else {
            pausedCount.current = 0;
            const delta = Date.now() - startTime.current;
            secondCount.current = delta / 1000;
            if (secondCount.current >= secondsToAnswer) {
              clearInterval(secondInterval);
              setTimerComplete(true);
            }
            // timeline seek placement, holds position while modal is up
            timelineCount.current = secondCount.current;
            setTimer(secondCount.current);
          }
        }, 100);
      }
    }
    return () => {
      clearInterval(secondInterval);
    };
  }, [isAnimationStopped, isModalActive, isReadyToShow, secondsToAnswer, setTimer, timerComplete]);

  useEffect(() => {
    // we overshoot the top of the screen so the wave animation exits entirely (else wobble moves in
    // and out of top of screen). this compensates for that extra distance to slow the animation
    const overflowCompensation = 0.3 * secondsToAnswer;
    let pauseTimeout;
    let boxTimeout;
    let debounceTimeout;

    if (isReadyToShow && isTimed) {
      // getBoundingClientBox was reporting the wrong height before this timeout
      boxTimeout = setTimeout(() => {
        const complete = () => {
          setIsAnimationStopped(true);
          setTimeIsUp(true);
          onUnanswered();
          setHideTimeoutScreen(true);

          pauseTimeout = setTimeout(() => {
            setTimerComplete(false);
            timelineRef.current.seek(0);
            timelineRef.current.clear();
            setTimeIsUp(false);
            onNextQuestion();
            setIsAnimationStopped(false);
            clearTimeout(pauseTimeout);
          }, 3000);
        };

        if (!started) {
          setStarted(true);
          timelineRef.current.fromTo(
            wrapper.current,
            {
              top: '120%',
            },
            {
              // overshooting the top of the screen to adjust for animatiobn wobble
              duration: secondsToAnswer + overflowCompensation,
              top: heightOffset,
              overwrite: 'auto',
            },
          );
        }

        const play = () => {
          setHideAnimation(false);
          timelineRef.current.play();
          timelineRef.current.seek(timelineCount.current);
        };

        if (isAnimationStopped) {
          timelineRef.current.pause();
          setHideAnimation(true);
        }

        if (timerComplete && !timeIsUp) {
          complete();
        }

        if (!timeIsUp && !isAnimationStopped) {
          play();
        }

        if (isModalActive) {
          timelineRef.current.pause();
        }
      }, 0);
    }

    return () => {
      clearTimeout(boxTimeout);
      clearTimeout(debounceTimeout);
    };
  }, [
    heightOffset,
    isAnimationStopped,
    isModalActive,
    isReadyToShow,
    onNextQuestion,
    onUnanswered,
    secondsToAnswer,
    setIsAnimationStopped,
    setTimeIsUp,
    started,
    timeIsUp,
    timerComplete,
  ]);

  return (
    <div className={cx(s.root, quizStarted ? s.quizStarted : '')}>
      <div className={s.waveContainer} ref={container}>
        <div className={cx(s.animationWrapper, hideAnimation ? s.hideAnimation : '')} ref={wrapper}>
          {isReadyToShow && (
            <RenderAnimation
              ariaLabel="quiz timer"
              animation="liquid"
              animationData={animationData}
              isStopped={isAnimationStopped}
            />
          )}
        </div>
      </div>
      {children}
      {hasTimedOut.current && (
        <div className={cx(s.timeUpOverflow, hideTimeoutScreen ? s.hideTimeout : '')}>
          <div className={s.timeUp}>
            <div className={s.text}>Time&apos;s up!</div>
          </div>
        </div>
      )}
    </div>
  );
};

QuizTimer.propTypes = propTypes;
export default QuizTimer;
